import { useState, useEffect, useCallback, useRef } from "react";
import { Settings, Maximize, AlertCircle, Sparkles } from "lucide-react";
import { Alert, Button, IconButton } from "@mui/material";
import { CameraSelector } from "./components/CameraSelector";
import { DeepARSettings } from "./components/DeepARSettings";
import { useCamera } from "./hooks/useCamera";
import { useDeepAR } from "./hooks/useDeepAR";

const ARCamera = ({ eventId }) => {
  const [showSettings, setShowSettings] = useState(false);
  const [showDeepARSettings, setShowDeepARSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMouseInactive, setIsMouseInactive] = useState(false);
  const mouseTimerRef = useRef(null);

  const {
    stream,
    devices,
    selectedDeviceId,
    isLoading: cameraLoading,
    error: cameraError,
    videoRef,
    switchCamera,
    getDevices,
  } = useCamera({ video: true, audio: false });

  const {
    canvasRef,
    isInitialized: deepARInitialized,
    isLoading: deepARLoading,
    error: deepARError,
    currentEffect,
    longDistanceTracking,
    toggleLongDistanceTracking,
    loadEffect,
    clearEffect,
    setVideoElement,
    resizeCanvas,
  } = useDeepAR({
    canvasWidth: 1920,
    canvasHeight: 1080,
    eventId: eventId, // Pass eventId to enable localStorage functionality
  });

  // Handle mouse activity for hiding UI
  const handleMouseActivity = useCallback(() => {
    setIsMouseInactive(false);
    if (mouseTimerRef.current) {
      clearTimeout(mouseTimerRef.current);
    }

    const timer = setTimeout(() => {
      setIsMouseInactive(true);
    }, 5000); // Hide UI after 5 seconds of inactivity

    mouseTimerRef.current = timer;
  }, []);

  // Setup mouse activity listeners
  useEffect(() => {
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];

    events.forEach((event) => {
      document.addEventListener(event, handleMouseActivity);
    });

    // Initial timer
    handleMouseActivity();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleMouseActivity);
      });
      if (mouseTimerRef.current) {
        clearTimeout(mouseTimerRef.current);
      }
    };
  }, [handleMouseActivity]);

  // Connect video to DeepAR when both are ready
  useEffect(() => {
    if (videoRef.current && deepARInitialized && stream) {
      setVideoElement(videoRef.current);
    }
  }, [videoRef, deepARInitialized, stream, setVideoElement]);

  // Handle window resize for canvas
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        resizeCanvas(rect.width, rect.height);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizeCanvas]);

  // Handle fullscreen toggle
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error("Error toggling fullscreen:", err);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "F11") {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === "s" && e.ctrlKey) {
        e.preventDefault();
        setShowSettings(!showSettings);
      } else if (e.key === "e" && e.ctrlKey) {
        e.preventDefault();
        setShowDeepARSettings(!showDeepARSettings);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [showSettings, showDeepARSettings]);

  const isLoading = cameraLoading || deepARLoading;
  const error = cameraError || deepARError;

  return (
    <div
      className={`video-container ${isMouseInactive ? "inactive" : ""}`}
      style={{ cursor: isMouseInactive ? "none" : "auto" }}
    >
      {/* Hidden Video Element */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="absolute opacity-0 pointer-events-none"
        style={{ width: "1px", height: "1px" }}
      />

      {/* DeepAR Canvas */}
      <canvas
        ref={canvasRef}
        className="video-stream w-full h-full object-cover"
        width={1920}
        height={1080}
        style={{ display: "block" }}
      />

      {/* Overlay Controls - Hidden when mouse is inactive */}
      <div
        className={`overlay-controls transition-opacity duration-500 ${
          isMouseInactive ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
      >
        {/* Top Bar */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
          {/* Settings Menu */}
          <div
            className={`transition-all duration-300 ${
              showSettings
                ? "translate-x-0 opacity-100"
                : "-translate-x-full opacity-0"
            }`}
          >
            <div className="glass-panel rounded-xl p-4 min-w-[300px]">
              <h3 className="mb-3 text-white">Camera Settings</h3>
              <div className="space-y-3">
                <CameraSelector
                  devices={devices}
                  selectedDeviceId={selectedDeviceId}
                  onDeviceSelect={switchCamera}
                  isLoading={isLoading}
                />

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={getDevices}
                  sx={{ mt: 2 }}
                >
                  Refresh Devices
                </Button>
              </div>
            </div>
          </div>

          {/* DeepAR Settings */}
          {showDeepARSettings && (
            <div className="absolute top-0 right-0 z-20">
              <DeepARSettings
                currentEffect={currentEffect}
                onEffectSelect={loadEffect}
                onEffectClear={clearEffect}
                isLoading={deepARLoading}
                longDistanceTracking={longDistanceTracking}
                onToggleLongDistanceTracking={toggleLongDistanceTracking}
                onClose={() => setShowDeepARSettings(false)}
                eventId={eventId}
              />
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              size="sm"
              onClick={() => setShowDeepARSettings(!showDeepARSettings)}
              className={`glass-button rounded-xl p-3 transition-all duration-200 ${
                showDeepARSettings ? "bg-primary/20 text-primary" : ""
              }`}
            >
              <Sparkles className="h-5 w-5" />
            </IconButton>
            <IconButton
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
              className="glass-button rounded-xl p-3"
            >
              <Settings className="h-5 w-5" />
            </IconButton>
            <IconButton
              size="sm"
              onClick={toggleFullscreen}
              className="glass-button rounded-xl p-3 hover:scale-105 transition-all duration-200"
            >
              <Maximize className="h-5 w-5" />
            </IconButton>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
            <Alert
              severity="error"
              className="glass-panel border-destructive/20 max-w-md"
            >
              <AlertCircle className="h-4 w-4" />
              <div>
                <div className="font-medium mb-2">
                  {typeof error === "string" ? error : error.message}
                </div>

                {typeof error === "object" && error.suggestions && (
                  <div className="text-sm space-y-1">
                    <div className="font-medium">Try these solutions:</div>
                    <ul className="list-disc list-inside space-y-1">
                      {error.suggestions.map((suggestion, index) => (
                        <li key={index} className="text-xs">
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Alert>
          </div>
        )}

        {/* Loading Indicator */}
        {cameraLoading && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="glass-panel rounded-xl p-6 text-center">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-3"></div>
              <p className="text-sm text-muted-foreground">
                "Initializing camera..."
              </p>
            </div>
          </div>
        )}

        {/* Status Bar */}
        <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
          {isFullscreen && (
            <div className="bg-gray-900/95 backdrop-blur-md border border-gray-600/40 rounded-xl px-4 py-2">
              <span className="text-xs text-gray-200">
                F11: Fullscreen | Ctrl+S: Settings | Ctrl+E: Effects
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ARCamera;
